import { AfterViewInit, DestroyRef, Directive, ElementRef, inject, input, Renderer2 } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, filter, fromEvent, merge } from 'rxjs';

const DEBOUNCE_TIME = 200;

@Directive({
  selector: '[libAdjustContentTopPadding]',
  standalone: true,
})
export class AdjustContentTopPaddingDirective implements AfterViewInit {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  destroyRef = inject(DestroyRef);
  router = inject(Router);
  private headers!: HTMLElement | null;
  private main!: HTMLElement | null;
  private initialHeaderHeight = 0;
  private paddingCalculated = false;
  private mutationObserver?: MutationObserver;
  private calculationTimeout?: ReturnType<typeof setTimeout>;
  isHeaderHidden = input(false);
  isHeaderHidden$ = toObservable(this.isHeaderHidden);

  ngAfterViewInit(): void {
    this.headers = this.el.nativeElement.querySelector('.headers');
    this.main = this.el.nativeElement.querySelector('.main');

    // Calculate initial padding once
    this.calculateInitialPadding();

    // Set up observer to watch for component additions (like conditional @if components)
    this.setupComponentLoadObserver();

    // Listen to header hidden state changes for smooth padding animation
    this.isHeaderHidden$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isHidden) => {
      this.updatePaddingForHeaderState(isHidden);
    });

    // Only listen to resize and navigation events, not scroll animations
    merge(
      fromEvent(window, 'resize'),
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        debounceTime(DEBOUNCE_TIME),
        takeUntilDestroyed(this.destroyRef),
      ),
    )
      .pipe(debounceTime(DEBOUNCE_TIME), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.resetAndRecalculate();
      });
  }

  private calculateInitialPadding(): void {
    if (this.headers && this.main && !this.paddingCalculated) {
      this.waitForStableHeaderHeight();
    }
  }

  private resetAndRecalculate(): void {
    this.paddingCalculated = false;
    this.initialHeaderHeight = 0;
    if (this.calculationTimeout) {
      clearTimeout(this.calculationTimeout);
    }
    this.calculateInitialPadding();
  }

  private updatePaddingForHeaderState(isHidden: boolean): void {
    if (!this.main || !this.paddingCalculated) {
      return;
    }

    // Add smooth transition for padding changes
    this.renderer.setStyle(this.main, 'transition', 'padding-top 0.3s linear');

    if (isHidden) {
      // Header is hidden - remove padding smoothly
      this.renderer.setStyle(this.main, 'padding-top', '0px');
    } else {
      // Header is visible - restore padding smoothly
      this.renderer.setStyle(this.main, 'padding-top', `${this.initialHeaderHeight}px`);
    }
  }

  private waitForStableHeaderHeight(): void {
    let lastHeight = 0;
    let stableCount = 0;
    const requiredStableChecks = 2; // Reduced to 2 for faster response
    const checkInterval = 100; // Check every 100ms
    const maxWaitTime = 4000; // Increased to 4 seconds to wait for API calls
    let elapsedTime = 0;

    const checkHeight = () => {
      if (this.paddingCalculated) return; // Already calculated

      const currentHeight = this.headers?.offsetHeight || 0;

      // Check if Apple tertiary nav is ready (if present)
      const tertiaryNavReady = this.isAppleTertiaryNavReady();

      if (currentHeight === lastHeight && currentHeight > 0 && tertiaryNavReady) {
        stableCount++;
        if (stableCount >= requiredStableChecks) {
          // Height is stable and tertiary nav is ready, apply padding
          this.initialHeaderHeight = currentHeight;
          this.renderer.setStyle(this.main, 'padding-top', `${currentHeight}px`);
          this.paddingCalculated = true;
          return;
        }
      } else {
        stableCount = 0; // Reset counter if height changed or tertiary nav not ready
      }

      lastHeight = currentHeight;
      elapsedTime += checkInterval;

      if (elapsedTime < maxWaitTime) {
        this.calculationTimeout = setTimeout(checkHeight, checkInterval);
      } else {
        // Fallback: use current height even if not perfectly stable
        if (currentHeight > 0) {
          this.initialHeaderHeight = currentHeight;
          this.renderer.setStyle(this.main, 'padding-top', `${currentHeight}px`);
          this.paddingCalculated = true;
        }
      }
    };

    // Start checking after a small initial delay
    this.calculationTimeout = setTimeout(checkHeight, 200);
  }

  private isAppleTertiaryNavReady(): boolean {
    // Check if we're on an Apple page that would have tertiary nav
    const isApplePage =
      /^\/marketplace\/1032(\/|$)/.test(window.location.pathname) ||
      window.location.href.includes('brandId=1032') ||
      window.location.href.includes('apple') ||
      window.location.href.includes('mac') ||
      window.location.href.includes('ipad') ||
      window.location.href.includes('iphone') ||
      window.location.href.includes('watch') ||
      window.location.href.includes('airpods');

    if (!isApplePage) {
      return true; // Not an Apple page, so tertiary nav is "ready"
    }

    // For Apple pages, check if tertiary nav elements are present and loaded
    const tertiaryNav = this.headers?.querySelector('.navigation-menu__apple-nav__tertiary-grid');
    const tertiaryItems = this.headers?.querySelectorAll('.navigation-menu__apple-nav__tertiary-item');

    // If tertiary nav exists, check if it has items loaded
    if (tertiaryNav) {
      return tertiaryItems && tertiaryItems.length > 0;
    }

    // If no tertiary nav element found, it might not be needed for this page
    return true;
  }

  private setupComponentLoadObserver(): void {
    if (!this.headers) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      // Check if any new components were added to the headers
      const hasNewComponents = mutations.some(
        (mutation) => mutation.type === 'childList' && mutation.addedNodes.length > 0,
      );

      // Check specifically for tertiary nav changes
      const hasTertiaryNavChanges = mutations.some((mutation) => {
        if (mutation.type === 'childList') {
          return Array.from(mutation.addedNodes).some((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              return (
                element.classList?.contains('navigation-menu__apple-nav__tertiary-grid') ||
                element.querySelector?.('.navigation-menu__apple-nav__tertiary-grid') ||
                element.querySelector?.('.navigation-menu__apple-nav__tertiary-item')
              );
            }
            return false;
          });
        }
        return false;
      });

      if (hasNewComponents || hasTertiaryNavChanges) {
        // Always recalculate when new components are added, even if padding was already calculated
        setTimeout(() => {
          this.resetAndRecalculate();
        }, 300);
      }
    });

    // Observe the headers element for child additions and attribute changes
    this.mutationObserver.observe(this.headers, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    // Clean up observer when component is destroyed
    this.destroyRef.onDestroy(() => {
      this.mutationObserver?.disconnect();
      if (this.calculationTimeout) {
        clearTimeout(this.calculationTimeout);
      }
    });
  }
}
